import React from 'react';
import { Input } from 'reactstrap';
import {
    TermForm,
    GeneralDeferredPaymentsInformation,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { validatePercentageField } from '../../../../../../utils/percentFieldValidator';
import './TermsFormGeneralDeferredPaymentsBlock.scss';

export interface TermsFormGeneralDeferredPaymentsBlockProps {
    termForm: TermForm;
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
}

export const TermsFormGeneralDeferredPaymentsBlock: React.FC<
    TermsFormGeneralDeferredPaymentsBlockProps
> = ({
    termForm,
    readOnly = false,
    onChange,
    errors,
    isBuyerReview,
    isBuyerReviewConfirmed,
    fieldComments,
    onFieldCommentChange,
}) => {
    const info: GeneralDeferredPaymentsInformation | null | undefined =
        termForm.generalDeferredPaymentsInformation;

    const update = (patch: Partial<GeneralDeferredPaymentsInformation>) => {
        onChange?.({
            ...termForm,
            generalDeferredPaymentsInformation: {
                ...(info ?? {}),
                ...patch,
            },
        });
    };

    const getComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.comment ?? null;

    const showComments = isBuyerReview || !!isBuyerReviewConfirmed;

    const commentInput = (fieldKey: string) => {
        const comment = getComment(fieldKey);
        return (
            <Input
                type="textarea"
                value={comment ?? ''}
                readOnly={!isBuyerReview}
                onChange={(e) =>
                    onFieldCommentChange?.(fieldKey, e.target.value === '' ? null : e.target.value)
                }
                className="terms-form-comment-input"
                aria-label={`Comment for ${fieldKey}`}
            />
        );
    };

    const interestVal = info?.interestAppliedToDeferredPayments ?? '';
    const interestDisplayVal = interestVal !== '' ? `${interestVal}%` : '';

    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-general-deferred-payments-block">
            <div className="terms-form-block-name">General Deferred Payments Information</div>
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            <th>Payment Obligor</th>
                            <th style={{ width: '160px' }}>Assets Held by Obligor</th>
                            <th style={{ width: '250px' }}>Interest Applied to Deferred Payments</th>
                            {showComments && (
                                <th className="terms-form-col-comment">Comments</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.paymentObligor ?? ''}
                                    disabled={readOnly}
                                    onChange={(e) =>
                                        update({ paymentObligor: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Payment Obligor"
                                    invalid={errors?.has('paymentObligor')}
                                />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.assetsHeldByObligor ?? ''}
                                    disabled={readOnly}
                                    onChange={(e) =>
                                        update({ assetsHeldByObligor: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Assets Held by Obligor"
                                    invalid={errors?.has('assetsHeldByObligor')}
                                    style={{ width: '82px' }}
                                />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={interestDisplayVal}
                                    disabled={readOnly}
                                    onKeyDown={(event) => {
                                        if (validatePercentageField(event, interestVal)) event.preventDefault();
                                    }}
                                    onChange={(event) => {
                                        let value = event.target.value;
                                        if (!value.includes('%') && Number(value) === Number(interestVal)) {
                                            value = value.slice(0, -1);
                                        }
                                        const result = value.replace('%', '');
                                        update({ interestAppliedToDeferredPayments: result === '' ? null : result });
                                    }}
                                    className="form-control loi-form-input loi-form-percent-input"
                                    aria-label="Interest Applied to Deferred Payments"
                                    invalid={errors?.has('interestAppliedToDeferredPayments')}
                                />
                            </td>
                            {showComments && (
                                <td className="terms-form-col-comment">
                                    {commentInput(LoiTermFormFieldKeys.interestAppliedToDeferredPayments)}
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
